/**
 * Fait : les MOTS FRANÇAIS des documents — l'état d'un fichier pendant sa
 *   lecture, la nature d'un échec, le format déduit d'un nom, la taille et la
 *   limite dites en clair. Tout ce qui transforme une valeur de machine en
 *   phrase lisible vit ici, et nulle part ailleurs.
 *
 *   LES TABLES SONT CLOSES SUR LA LISTE DU PRODUIT (`Record<…>`) : le jour où
 *   un état ou une nature d'échec neuve arrivera, le typage refusera de
 *   compiler et l'épreuve criera. Un `switch` avec un cas par défaut, lui,
 *   aurait affiché poliment un mot anglais de machine à quelqu'un qui attend
 *   de savoir pourquoi son document est refusé.
 *
 * Ne fait pas : il n'appelle rien, n'affiche rien, et ne décide d'aucun droit.
 *   Il ne VALIDE pas non plus un fichier — la borne de taille est celle du
 *   produit, et c'est l'écran qui s'en sert pour refuser avant d'envoyer.
 */

import { NATURES_D_ECHEC_D_EXTRACTION, type NatureDEchecDExtraction } from '@aymoai/shared';
import {
  FORMATS_DE_FICHIER,
  TAILLE_MAXIMALE_FICHIER_OCTETS,
  type EtatDeFichier,
  type FormatDeFichier,
} from '@aymoai/shared/donnees';

/** Où en est un document, dit à qui regarde la liste. */
export const MOT_DE_L_ETAT: Readonly<Record<EtatDeFichier, string>> = {
  importe: 'Reçu, en attente de lecture',
  en_traitement: 'Lecture en cours',
  pret: 'Prêt à lire',
  refuse: 'Refusé',

  // Il ne s'affiche jamais dans la liste — le document en a disparu — mais
  // l'état existe dans le produit, et le direct l'annonce : il a son mot.
  supprime: 'Supprimé',
};

/** Pourquoi la lecture d'un document a échoué. Une phrase, pas un code. */
export const MOT_DE_L_ECHEC: Readonly<Record<NatureDEchecDExtraction, string>> = {
  trop_gros: 'Ce document dépasse la taille admise.',
  trop_de_pages: 'Ce document a plus de pages que la limite.',
  illisible: 'Ce document n’a pas pu être lu : il est abîmé, ou n’est pas du format annoncé.',
  delai_depasse: 'La lecture a pris trop de temps : elle a été arrêtée.',
  worker_perdu: 'La lecture s’est interrompue. Réessayez ; si cela recommence, dites-le.',
  sortie_reseau: 'La lecture a été arrêtée : ce document tentait de sortir sur le réseau.',
};

/**
 * La phrase d'un échec, y compris pour une nature que cet écran ne connaît
 * pas. Le serveur range la nature dans un texte libre : une valeur inattendue
 * est possible, et se taire laisserait « Refusé » tout seul, sans un pourquoi.
 */
export function motDeLEchec(nature: string | undefined): string {
  const connue = NATURES_D_ECHEC_D_EXTRACTION.find((admise) => admise === nature);

  return connue === undefined ? 'La lecture de ce document a échoué.' : MOT_DE_L_ECHEC[connue];
}

/**
 * Le format ANNONCÉ au serveur, déduit de l'extension du nom choisi.
 *
 * Le serveur ne devine jamais un format d'après un nom (liste blanche du
 * critère 32) : c'est l'appelant qui le déclare. L'écran le déduit donc ici,
 * et rend `null` quand il ne sait pas — auquel cas rien n'est envoyé.
 */
const FORMAT_PAR_EXTENSION: Readonly<Record<string, FormatDeFichier>> = {
  pdf: 'pdf',
  txt: 'txt',
  md: 'markdown',
  markdown: 'markdown',
  docx: 'docx',
};

/** Les extensions proposées au sélecteur de fichier — les mêmes, avec un point. */
export const EXTENSIONS_ADMISES = Object.keys(FORMAT_PAR_EXTENSION)
  .map((extension) => `.${extension}`)
  .join(',');

/** Le format d'un nom de fichier, ou `null` s'il n'est pas des quatre admis. */
export function formatDuNom(nom: string): FormatDeFichier | null {
  const morceaux = nom.toLowerCase().split('.');
  if (morceaux.length < 2) return null;

  return FORMAT_PAR_EXTENSION[morceaux[morceaux.length - 1] ?? ''] ?? null;
}

/** Les quatre formats, écrits comme on les lit. Tirés de la liste du produit. */
export const FORMATS_EN_CLAIR = FORMATS_DE_FICHIER.map((format) =>
  format === 'markdown' ? 'Markdown' : format.toUpperCase(),
).join(', ');

/**
 * Une taille en octets, dite en français.
 *
 * L'unité monte tant que le nombre reste grand : « 10 Mo » se lit, pas
 * « 10485760 octets ». La virgule est celle du français, jamais le point.
 */
export function tailleLisible(octets: number): string {
  // « 1 octets » est une faute de français que le refus d'un fichier hors
  // limite peut afficher : l'excès y est parfois d'un seul octet.
  if (octets === 1) return '1 octet';

  const unites = ['octets', 'ko', 'Mo'] as const;
  let valeur = octets;
  let rang = 0;

  while (valeur >= 1024 && rang < unites.length - 1) {
    valeur /= 1024;
    rang += 1;
  }

  const arrondi = rang === 0 ? String(Math.round(valeur)) : valeur.toFixed(1).replace('.', ',');

  return `${arrondi.replace(',0', '')} ${unites[rang]}`;
}

/** La limite de taille, dite avant qu'on choisisse — jamais seulement au refus. */
export const LIMITE_EN_CLAIR = tailleLisible(TAILLE_MAXIMALE_FICHIER_OCTETS);
